// Family Context - Manages current family and member state
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useQuery, useSubscription } from '@apollo/client/react';
import {
  GET_CURRENT_USER,
  GET_FAMILY,
  SUBSCRIBE_HOME_STATS,
  SUBSCRIBE_PERSON_DATA_STATS,
  GET_ALL_FAMILY_DATA,
  GET_FAMILY_MEMBERS_DATA,
} from '@common/graphql';
import { useAuth } from './AuthContext';
import { getMediaFilesByFileId } from '../services/api/familypal';

// Types
export interface PersonProfile {
  first: string;
  last: string;
  email?: string;
  phone?: string;
  gender?: 'M' | 'F' | 'O';
  birthDate?: string;
  photoURL?: string;
  profilePhotoId?: string;
  role?: string;
}

export interface Person {
  id: string;
  uid?: string;
  familyIds: string[];
  createdAt: string;
  modifiedAt: string;
  data: {
    profile: PersonProfile;
  };
  status?: string;
}

export interface FamilyMember {
  id: string;
  color?: string;
  role?: string;
}

export interface FamilyData {
  name: string;
  members: Record<string, FamilyMember>;
}

export interface Family {
  id: string;
  memberIds: string[];
  createdAt: string;
  modifiedAt: string;
  data: FamilyData;
  status?: string;
}

// Lightweight family info for selection (from GET_CURRENT_USER)
export interface FamilyInfo {
  id: string;
  name: string;
  slug?: string;
}

export interface AppStats {
  events: number;
  messages: number;
  notes: number;
  lists: number;
  photos: number;
  videos: number;
  quotes: number;
  contacts: number;
  activities: number;
  schools: number;
  classes: number;
  assignments: number;
  illnesses: number;
  allergies: number;
  doses: number;
  chores: number;
  routines: number;
  rewards: number;
  albums: number;
  tally: { owedByMe: number; owedToMe: number };
}

interface FamilyContextValue {
  // Current user's person record
  person: Person | null;
  personId: string | null;

  // All families the user belongs to (lightweight info for selectors)
  allFamilies: FamilyInfo[];
  familyIds: string[];

  // Currently selected/active family (full details)
  families: Family[];
  currentFamily: Family | null;
  currentFamilyId: string | null;
  setCurrentFamilyId: (id: string) => void;

  // Family members (of current family)
  members: Record<string, Person>;
  memberList: Person[];

  // Stats
  stats: AppStats | null;

  // Loading states
  isLoading: boolean;
  error: Error | null;

  // Helpers
  getMember: (personId: string) => Person | null;
  getMemberName: (personId: string) => string;
  getMemberColor: (personId: string) => string;
  getMemberRole: (personId: string) => string;
  getMemberPhoto: (personId: string) => string | undefined;
  getMemberPhotoId: (personId: string) => string | undefined;
  refetch: () => void;
}

const defaultStats: AppStats = {
  events: 0,
  messages: 0,
  notes: 0,
  lists: 0,
  photos: 0,
  videos: 0,
  quotes: 0,
  contacts: 0,
  activities: 0,
  schools: 0,
  classes: 0,
  assignments: 0,
  illnesses: 0,
  allergies: 0,
  doses: 0,
  chores: 0,
  routines: 0,
  rewards: 0,
  albums: 0,
  tally: { owedByMe: 0, owedToMe: 0 },
};

const FamilyContext = createContext<FamilyContextValue | undefined>(undefined);

const CURRENT_FAMILY_KEY = 'familypal_current_family';

// Helper to normalize file entries from albums
const normalizeFileEntry = (entry: any): { id: string; type: 'image' | 'video' } => {
  if (typeof entry === 'string') {
    return { id: entry, type: 'image' };
  }
  return {
    id: entry.id || entry.fileId || '',
    type: entry.type === 'video' ? 'video' : 'image',
  };
};

interface FamilyProviderProps {
  children: ReactNode;
}

export const FamilyProvider: React.FC<FamilyProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentFamilyId, setCurrentFamilyIdState] = useState<string | null>(() => {
    return localStorage.getItem(CURRENT_FAMILY_KEY);
  });

  // Query current user to get their person record and families
  const {
    data: userData,
    loading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery(GET_CURRENT_USER, {
    variables: { userId: user?.id },
    skip: !isAuthenticated || !user?.id,
  });

  // Extract person and family IDs from user data
  const person = useMemo(() => {
    const userPerson = (userData as any)?.user_by_pk?.person;
    if (!userPerson) {
      return null;
    }
    return {
      id: userPerson.id,
      uid: user?.id,
      familyIds: userPerson.families?.map((f: any) => f.family?.id) || [],
      createdAt: userPerson.created_at || new Date().toISOString(),
      modifiedAt: userPerson.modified_at || new Date().toISOString(),
      data: userPerson.data || { profile: { first: '', last: '' } },
      status: 'A',
    } as Person;
  }, [userData, user?.id]);

  const familyIds = useMemo(() => person?.familyIds || [], [person]);
  const personId = person?.id || null;

  // Extract all families from user data (for family selector)
  const allFamilies = useMemo<FamilyInfo[]>(() => {
    const userPerson = (userData as any)?.user_by_pk?.person;
    if (!userPerson?.families) return [];
    return userPerson.families
      .map((f: any) => ({
        id: f.family?.id,
        name: f.family?.name || f.family?.data?.name || 'Unnamed Family',
        slug: f.family?.slug,
      }))
      .filter((f: FamilyInfo) => f.id); // Filter out any with missing IDs
  }, [userData]);

  // Query family data
  const {
    data: familyData,
    loading: familyLoading,
    refetch: refetchFamily,
  } = useQuery(GET_FAMILY, {
    variables: { id: currentFamilyId },
    skip: !currentFamilyId,
  });

  // Parse family and members from query
  const families = useMemo(() => {
    const data = familyData as any;
    // Handle both array and single object responses
    const familyResult = data?.family;
    if (!familyResult) {
      return [];
    }
    // If it's an array, use the first element; otherwise use as-is
    const f = Array.isArray(familyResult) ? familyResult[0] : familyResult;
    if (!f) {
      return [];
    }
    return [{
      id: f.id,
      memberIds: f.members?.map((m: any) => m.person_id) || [],
      createdAt: f.created_at,
      modifiedAt: f.modified_at,
      data: f.data || { name: '', members: {} },
      status: f.status,
    } as Family];
  }, [familyData]);

  const currentFamily = families[0] || null;

  // Parse members from family data
  const members = useMemo(() => {
    const data = familyData as any;
    const familyResult = data?.family;
    // Handle both array and single object responses
    const f = Array.isArray(familyResult) ? familyResult[0] : familyResult;
    if (!f?.members) {
      return {};
    }
    const membersMap: Record<string, Person> = {};
    f.members.forEach((m: any) => {
      const personData = m.person;
      if (personData) {
        // Build profile from direct person fields (GraphQL returns flat structure)
        // For photo URL: storage:// URIs aren't valid HTTP URLs, use mini_preview (base64) instead
        const photo = m.person.profile_photo;
        let photoURL = '';
        if (photo?.mini_preview) {
          // mini_preview is base64 encoded - this works in browsers
          photoURL = `data:image/jpeg;base64,${photo.mini_preview}`;
        } else if (photo?.uri && !photo.uri.startsWith('storage://')) {
          // Only use uri if it's a valid HTTP URL
          photoURL = photo.uri;
        }
        const profile: PersonProfile = {
          first: m.person.first_name || '',
          last: m.person.last_name || '',
          email: m.person.email,
          phone: m.person.phone,
          gender: m.person.gender,
          birthDate: m.person.birth_date,
          photoURL,
          profilePhotoId: m.person.profile_photo_id,
          role: m.role,
        };
        // user can be an array or single object
        // Debug: log the raw user field to understand its structure
        console.log('FamilyContext: Raw user field for', m.person.first_name, ':', JSON.stringify(m.person.user));
        const userObj = Array.isArray(m.person.user) ? m.person.user[0] : m.person.user;
        const uid = userObj?.id;
        console.log('FamilyContext: Extracted uid for', m.person.first_name, ':', uid);
        membersMap[m.person_id] = {
          id: m.person.id,
          uid,
          familyIds: [], // Not needed for display
          createdAt: m.person.created_at || '',
          modifiedAt: m.person.modified_at || '',
          data: { profile },
          status: 'A',
        } as Person;
      }
    });
    // Log parsed members summary
    console.log('FamilyContext: Parsed members:', Object.values(membersMap).map(m => ({
      name: `${m.data?.profile?.first} ${m.data?.profile?.last}`.trim(),
      uid: m.uid,
      hasPhoto: !!m.data?.profile?.profilePhotoId,
    })));
    return membersMap;
  }, [familyData]);

  const memberList = useMemo(() => Object.values(members), [members]);

  // State for fetched profile photo URLs (fileId -> url)
  const [profilePhotoUrls, setProfilePhotoUrls] = useState<Record<string, string>>({});

  // Fetch profile photo URLs for all members
  useEffect(() => {
    const photoIds = memberList
      .map((m) => m.data?.profile?.profilePhotoId)
      .filter((id): id is string => !!id);

    if (photoIds.length === 0) {
      return;
    }

    console.log('FamilyContext: Fetching profile photos for IDs:', photoIds);

    const fetchUrls = async () => {
      const urls: Record<string, string> = {};
      await Promise.all(
        photoIds.map(async (fileId) => {
          try {
            const response = await getMediaFilesByFileId({
              path: { file_id: fileId },
            } as any);
            if (response.data) {
              const result = response.data as any;
              // Prefer thumbnail for avatars
              const url = result?.thumbnail_uri || result?.thumbnailUri || result?.uri || result?.download_url;
              if (url) {
                urls[fileId] = url;
              }
            } else if (response.error) {
              console.warn('FamilyContext: Error fetching photo', fileId, ':', response.error);
            }
          } catch (err) {
            console.warn('Failed to fetch profile photo:', fileId, err);
          }
        })
      );
      console.log('FamilyContext: Fetched profile photo URLs:', urls);
      setProfilePhotoUrls(urls);
    };

    fetchUrls();
  }, [memberList]);

  // Create a map from user ID (uid) to person ID for chat lookups
  // IMPORTANT: Use the key from members map, not member.id, since those could differ
  const uidToPersonId = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(members).forEach(([key, member]) => {
      if (member.uid) {
        map[member.uid] = key; // Map uid to the key used in members map
      }
    });
    console.log('FamilyContext: uidToPersonId map built:', map);
    return map;
  }, [members]);

  // Direct maps from user ID to name/photo/color (matching mobile approach)
  // This is used for chat where sender_id is the user ID
  const memberNamesByUid = useMemo(() => {
    const names: Record<string, string> = {};
    Object.values(members).forEach((member) => {
      if (member.uid) {
        const name = member.data?.profile?.first
          ? `${member.data.profile.first}${member.data.profile.last ? ' ' + member.data.profile.last : ''}`
          : 'Unknown';
        names[member.uid] = name;
      }
    });
    console.log('FamilyContext: memberNamesByUid map built:', names);
    return names;
  }, [members]);

  const memberPhotoIdsByUid = useMemo(() => {
    const photoIds: Record<string, string> = {};
    Object.values(members).forEach((member) => {
      if (member.uid && member.data?.profile?.profilePhotoId) {
        photoIds[member.uid] = member.data.profile.profilePhotoId;
      }
    });
    console.log('FamilyContext: memberPhotoIdsByUid map built:', photoIds);
    return photoIds;
  }, [members]);

  // Map uid to color from family member data
  const memberColorsByUid = useMemo(() => {
    const colors: Record<string, string> = {};
    Object.entries(members).forEach(([personKey, member]) => {
      if (member.uid) {
        const familyMember = currentFamily?.data?.members?.[personKey];
        if (familyMember?.color) {
          colors[member.uid] = familyMember.color;
        }
      }
    });
    console.log('FamilyContext: memberColorsByUid map built:', colors);
    return colors;
  }, [members, currentFamily]);

  // Get person IDs for health stats
  const memberPersonIds = useMemo(() => {
    return Object.keys(members);
  }, [members]);

  // Subscribe to family data for stats
  // Note: family_id filtering is handled by Hasura permissions
  const { data: familyStatsData } = useSubscription(SUBSCRIBE_HOME_STATS, {});

  // Fallback query if subscription not working
  // Note: family_id filtering is handled by Hasura permissions
  const { data: familyStatsQueryData } = useQuery(GET_ALL_FAMILY_DATA, {
    fetchPolicy: 'cache-and-network',
  });

  // Subscribe to person data for health stats
  const { data: personStatsData } = useSubscription(SUBSCRIBE_PERSON_DATA_STATS, {
    variables: { person_ids: memberPersonIds },
    skip: memberPersonIds.length === 0,
  });

  // Fallback query for person data
  const { data: personStatsQueryData } = useQuery(GET_FAMILY_MEMBERS_DATA, {
    variables: { person_ids: memberPersonIds },
    skip: memberPersonIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  // Compute stats from fetched data (like mobile app)
  const stats = useMemo(() => {
    // Use subscription data if available, otherwise use query data
    const familyItems =
      (familyStatsData as any)?.family_data ||
      (familyStatsQueryData as any)?.family_data ||
      [];
    const personItems =
      (personStatsData as any)?.person_data ||
      (personStatsQueryData as any)?.person_data ||
      [];

    // Count family data by type
    const countByType = (type: string) =>
      familyItems.filter((item: any) => item.data?.type === type).length;

    // Count contacts by category
    const countContactsByCategory = (category: string) =>
      familyItems.filter((item: any) =>
        item.data?.type === 'contact' &&
        item.data?.data?.category?.toLowerCase() === category
      ).length;

    // Count contacts excluding schools
    const countNonSchoolContacts = () =>
      familyItems.filter((item: any) =>
        item.data?.type === 'contact' &&
        item.data?.data?.category?.toLowerCase() !== 'school'
      ).length;

    // Count person data by type (type is nested under item.data.type)
    const countPersonByType = (type: string) =>
      personItems.filter((item: any) => item.data?.type === type).length;

    // Count photos/videos from albums
    // Use query data (has full nested data) since subscription only fetches type
    const fullFamilyItems =
      (familyStatsQueryData as any)?.family_data || familyItems;
    let photoCount = 0;
    let videoCount = 0;
    fullFamilyItems
      .filter((item: any) => item.data?.type === 'album')
      .forEach((item: any) => {
        const entries = item.data?.data?.fileIds;
        if (Array.isArray(entries)) {
          for (const entry of entries) {
            const normalized = normalizeFileEntry(entry);
            if (normalized.type === 'image') photoCount++;
            else if (normalized.type === 'video') videoCount++;
          }
        }
      });

    // Count incomplete assignments from classes
    let incompleteAssignments = 0;
    familyItems
      .filter((item: any) => item.data?.type === 'class')
      .forEach((item: any) => {
        const assignments = item.data?.data?.assignments || [];
        incompleteAssignments += assignments.filter((a: any) => !a.completed).length;
      });

    // Calculate money tally
    const calculateTally = () => {
      if (!personId) return { owedByMe: 0, owedToMe: 0 };

      const expenses = familyItems.filter((item: any) => item.data?.type === 'expense');
      const payments = familyItems.filter((item: any) => item.data?.type === 'payment');

      let myExpenseShares = 0;
      for (const item of expenses) {
        const exp = item.data?.data;
        if (!exp?.member_ids?.includes(personId)) continue;
        const amount = exp.amount || 0;
        if (exp.split_type === 'custom' && exp.custom_splits?.[personId]) {
          myExpenseShares += parseFloat(exp.custom_splits[personId]) || 0;
        } else if (exp.member_ids.length > 0) {
          myExpenseShares += amount / exp.member_ids.length;
        }
      }

      let myPayments = 0;
      for (const item of payments) {
        const pmt = item.data?.data;
        if (pmt?.from_member_id === personId) {
          myPayments += pmt.amount || 0;
        }
      }

      const balance = myExpenseShares - myPayments;
      return {
        owedByMe: Math.max(0, balance),
        owedToMe: Math.max(0, -balance),
      };
    };

    return {
      events: 0, // TODO: Add today's events count
      messages: 0, // TODO: Add unread messages count
      notes: countByType('note'),
      lists: countByType('list'),
      photos: photoCount,
      videos: videoCount,
      quotes: countByType('quote'),
      contacts: countNonSchoolContacts(),
      activities: countPersonByType('activity'),
      schools: countContactsByCategory('school'),
      classes: countPersonByType('class'),
      assignments: incompleteAssignments,
      illnesses: countPersonByType('illness'),
      allergies: countPersonByType('allergy'),
      doses: countPersonByType('medication'),
      chores: countPersonByType('chore'),
      routines: countPersonByType('routine'),
      rewards: countByType('reward'),
      albums: countByType('album'),
      tally: calculateTally(),
    };
  }, [familyStatsData, familyStatsQueryData, personStatsData, personStatsQueryData, personId]);

  // Auto-select first family if none selected
  useEffect(() => {
    if (!currentFamilyId && familyIds.length > 0) {
      setCurrentFamilyIdState(familyIds[0]);
      localStorage.setItem(CURRENT_FAMILY_KEY, familyIds[0]);
    }
  }, [currentFamilyId, familyIds]);

  const setCurrentFamilyId = (id: string) => {
    setCurrentFamilyIdState(id);
    localStorage.setItem(CURRENT_FAMILY_KEY, id);
  };

  // Helper functions
  const getMember = useCallback((id: string): Person | null => {
    // Try direct lookup by person ID first
    let member = members[id];
    // If not found, try lookup by user ID (uid)
    if (!member && uidToPersonId[id]) {
      member = members[uidToPersonId[id]];
    }
    return member || null;
  }, [members, uidToPersonId]);

  const getMemberName = useCallback((id: string): string => {
    // First try direct uid lookup (for chat sender_id which is user ID)
    if (memberNamesByUid[id]) {
      return memberNamesByUid[id];
    }

    // Try direct lookup by person ID
    let member = members[id];
    if (member) {
      const profile = member.data?.profile;
      if (profile?.first) {
        return `${profile.first}${profile.last ? ' ' + profile.last : ''}`;
      }
    }

    // If not found, try lookup by user ID (uid) through person ID mapping
    if (!member && uidToPersonId[id]) {
      member = members[uidToPersonId[id]];
      if (member) {
        const profile = member.data?.profile;
        if (profile?.first) {
          return `${profile.first}${profile.last ? ' ' + profile.last : ''}`;
        }
      }
    }

    return 'Unknown';
  }, [members, uidToPersonId, memberNamesByUid]);

  const getMemberColor = useCallback((id: string): string => {
    // First try direct uid lookup
    if (memberColorsByUid[id]) {
      return memberColorsByUid[id];
    }
    // Try direct lookup by person ID, then by uid mapping
    const personId = uidToPersonId[id] || id;
    const familyMember = currentFamily?.data?.members?.[personId];
    return familyMember?.color || '#96D0F0';
  }, [currentFamily, uidToPersonId, memberColorsByUid]);

  const getMemberRole = useCallback((id: string): string => {
    const personId = uidToPersonId[id] || id;
    const familyMember = currentFamily?.data?.members?.[personId];
    return familyMember?.role || '';
  }, [currentFamily, uidToPersonId]);

  const getMemberPhoto = useCallback((id: string): string | undefined => {
    // First try direct uid lookup for photo ID
    const photoIdFromUid = memberPhotoIdsByUid[id];
    if (photoIdFromUid && profilePhotoUrls[photoIdFromUid]) {
      return profilePhotoUrls[photoIdFromUid];
    }

    // Try direct lookup by person ID, then by uid
    let member = members[id];
    if (!member && uidToPersonId[id]) {
      member = members[uidToPersonId[id]];
    }
    if (!member) {
      return undefined;
    }

    const photoId = member.data?.profile?.profilePhotoId;
    // First try fetched URL, then fall back to mini preview
    if (photoId && profilePhotoUrls[photoId]) {
      return profilePhotoUrls[photoId];
    }
    return member.data?.profile?.photoURL; // base64 mini preview fallback
  }, [members, uidToPersonId, profilePhotoUrls, memberPhotoIdsByUid]);

  // Get profile photo ID for a member (by person ID or user ID)
  // This is used with MemberAvatar component which fetches the URL itself
  const getMemberPhotoId = useCallback((id: string): string | undefined => {
    // First try direct uid lookup
    if (memberPhotoIdsByUid[id]) {
      return memberPhotoIdsByUid[id];
    }

    // Try direct lookup by person ID, then by uid
    let member = members[id];
    if (!member && uidToPersonId[id]) {
      member = members[uidToPersonId[id]];
    }
    return member?.data?.profile?.profilePhotoId;
  }, [members, uidToPersonId, memberPhotoIdsByUid]);

  const refetch = () => {
    refetchUser();
    if (currentFamilyId) refetchFamily();
  };

  const isLoading = userLoading || familyLoading;
  const error = userError || null;

  const value: FamilyContextValue = {
    person,
    personId,
    allFamilies,
    families,
    familyIds,
    currentFamily,
    currentFamilyId,
    setCurrentFamilyId,
    members,
    memberList,
    stats,
    isLoading,
    error,
    getMember,
    getMemberName,
    getMemberColor,
    getMemberRole,
    getMemberPhoto,
    getMemberPhotoId,
    refetch,
  };

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
};

// Hook to use family context
export const useFamily = (): FamilyContextValue => {
  const context = useContext(FamilyContext);
  if (context === undefined) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};

// Hook that requires a family to be selected
export const useRequireFamily = (): FamilyContextValue & { currentFamily: Family; currentFamilyId: string } => {
  const family = useFamily();
  if (!family.currentFamily || !family.currentFamilyId) {
    throw new Error('No family selected');
  }
  return family as FamilyContextValue & { currentFamily: Family; currentFamilyId: string };
};
